import {
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { PostsVirais } from '@/model';
import { usePutPostsViraisMutation } from '@/services/queries/social-media';
import { ModalFormData, modalFormSchema } from './schemas';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PostsViraisEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  item: PostsVirais;
  onDone?: () => void;
}

const MAX_POSTS = 5;

export const PostsViraisEditModal = ({ isOpen, onClose, item, onDone }: PostsViraisEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { control, register, handleSubmit, reset } = useForm<ModalFormData>({
    resolver: zodResolver(modalFormSchema),
    defaultValues: {
      team_uuid: item.team_uuid || '',
      urls: item.urls.map(url => ({ value: url })),
      enabled: item.enabled,
    },
  });

  const {
    fields: urlFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'urls',
  });

  const putMutation = usePutPostsViraisMutation();

  const handleConfirm = useCallback(() => {
    handleSubmit(data => {
      const payload = {
        team_uuid: data.team_uuid,
        enabled: data.enabled,
        urls: data.urls.map(u => u.value),
      };

      putMutation.mutate(
        { id: item.uuid, payload },
        {
          onSuccess: () => {
            toast({
              title: t('pages.application_social_media.social_media_changes_saved'),
              status: 'success',
            });
            onDone?.();
            onClose();
          },
          onError: () => {
            toast({
              title: t('pages.application_social_media.social_media_save_failed'),
              status: 'error',
            });
          },
        },
      );
    })();
  }, [handleSubmit, item.uuid, putMutation, toast, onClose, onDone, t]);

  useEffect(() => {
    if (isOpen) {
      reset({
        team_uuid: item.team_uuid || '',
        urls: item.urls.map(url => ({ value: url })),
        enabled: item.enabled,
      });
    }
  }, [isOpen, item, reset]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justify='space-between' align='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_social_media.social_media_post_viral.modal_edit.edit_title')}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <Flex direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_social_media.social_media_post_viral.modal_edit.team')}
              placeholder={t('pages.application_social_media.social_media_post_viral.modal_edit.team')}
              showFlag
              width='100%'
              usePortal={false}
            />

            <Flex direction='column' gap={4}>
              {urlFields.map((field, index) => (
                <Flex key={field.id} direction={{ base: 'column', md: 'row' }} gap={3} align='center'>
                  <Input
                    placeholder={t('pages.application_social_media.social_media_post_viral.modal_edit.placeholder_url')}
                    {...register(`urls.${index}.value`)}
                  />
                  <IconButton
                    icon={<PiTrash />}
                    aria-label='Remove'
                    size='sm'
                    colorScheme='red'
                    variant='ghost'
                    onClick={() => remove(index)}
                  />
                </Flex>
              ))}

              {urlFields.length < MAX_POSTS && (
                <Button
                  onClick={() => append({ value: '' })}
                  leftIcon={<PiPlus />}
                  variant='link'
                  color='brand.500'
                  fontWeight='normal'
                  alignSelf='flex-start'
                >
                  {t('pages.application_social_media.social_media_post_viral.modal_edit.add_button')}
                </Button>
              )}

              {urlFields.length >= MAX_POSTS && (
                <Text fontSize='xs' color='gray.500'>
                  {t('pages.application_social_media.social_media_post_viral.modal_edit.limit_reached', {
                    count: MAX_POSTS,
                  })}
                </Text>
              )}
            </Flex>

            <Flex align='center' gap={3}>
              <Text fontWeight='medium'>
                {t('pages.application_social_media.social_media_post_viral.modal_edit.enable_area')}
              </Text>
              <Controller
                control={control}
                name='enabled'
                render={({ field }) => <Switch size='sm' isChecked={field.value} onChange={field.onChange} />}
              />
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='flex-end' gap={4}>
          <Button variant='outlined' color='gray.600' onClick={onClose}>
            {t('pages.application_social_media.social_media_post_viral.modal_edit.create_cancel')}
          </Button>
          <Button onClick={handleConfirm} px={10}>
            {t('pages.application_social_media.social_media_post_viral.modal_edit.create_save')}
          </Button>
        </ModalFooter>

        <CenteredSpinner isLoading={putMutation.isLoading} />
      </ModalContent>
    </Modal>
  );
};
